import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Alert, TextField, IconButton } from '@mui/material';
import { Button } from 'components/index';
import SelectInput from '../SelectInput';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Close } from '@mui/icons-material';
import NumericFormatInput from '../general/NumericFormatInput';
import { ProjectLineItemDTO } from '../project/project.type';

interface Props {
  open: boolean;
  onClose: () => void;
  lineItem: ProjectLineItemDTO | null;
  updateLineItem: (lineItem: ProjectLineItemDTO) => void;
  divisions: Division[];
}

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];

const ProjectAnalysisLineItemEditDialog: React.FC<Props> = ({ open, onClose, lineItem: initialLineItem, updateLineItem, divisions }) => {
  const initialOrDefaultValues = {
    originalBudget: initialLineItem?.originalBudget ?? 0,
    quantity: initialLineItem?.quantity ?? 0,
    unit: initialLineItem?.lineItem?.unit ?? null,
    division: initialLineItem?.lineItem?.division ?? null,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      originalBudget: yup.number().required('Required').typeError('Must be a number'),
      quantity: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
    }),
    onSubmit: values => {
      if (!initialLineItem) return;
      updateLineItem({
        ...initialLineItem,
        ...values,
        lineItem: {
          ...initialLineItem.lineItem,
          unit: values.unit ?? initialLineItem.lineItem.unit,
          division: values.division ?? initialLineItem.lineItem.division,
        },
      });
      formik.resetForm();
      onClose();
    },
  });

  const updateDivision = (newVal: string | null) => {
    if (!initialLineItem) return;
    const newDivision = newVal ? divisions.find(division => division.id === newVal) : null;
    formik.setFieldValue('division', newDivision ?? null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {initialLineItem?.lineItem.description ?? 'Edit Line Item'}
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {initialLineItem ? (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} py={2}>
              <TextField
                name='originalBudget'
                label='Original Budget'
                value={formik.values.originalBudget}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.originalBudget && Boolean(formik.errors.originalBudget)}
                helperText={formik.touched.originalBudget && formik.errors.originalBudget}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
              />
              <TextField
                name='quantity'
                label='Quantity'
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                helperText={formik.touched.quantity && formik.errors.quantity}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
              />
              <SelectInput
                label='Unit'
                selected={formik.values.unit ?? ''}
                setSelected={newVal => {
                  formik.setFieldValue('unit', newVal === '' ? null : newVal);
                }}
                list={unitsList}
              />
              <SelectDivisionFromGiven
                label='Division'
                divisions={divisions}
                selectedDivision={formik.values.division?.id ?? null}
                setSelectedDivision={updateDivision}
              />
              <Alert severity='warning'>
                You need to click <b>Submit Changes</b> to avoid losing your changes!
              </Alert>
            </Stack>
          </form>
        ) : (
          <Alert severity='error'>No LineItem Selected</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
          Save Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectAnalysisLineItemEditDialog;
